import React, { useState, useEffect } from "react";
import { db } from "../firebase";
import {
  collection,
  addDoc,
  query,
  where,
  doc,
  updateDoc,
  getDocs,
} from "firebase/firestore";
import "./AddEditScreenModal.css";

function AddEditScreenModal({ isOpen, onClose, existingScreen }) {
  const [name, setName] = useState("");
  const [pairingCode, setPairingCode] = useState("");
  const [nowPlayingType, setNowPlayingType] = useState("playlist");
  const [nowPlayingId, setNowPlayingId] = useState("");
  const [availableContent, setAvailableContent] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    if (existingScreen) {
      setName(existingScreen.name);
      setPairingCode(existingScreen.pairingCode || "");
      setNowPlayingType(existingScreen.nowPlaying?.type || "playlist");
      setNowPlayingId(existingScreen.nowPlaying?.id || "");
    } else {
      setName("");
      setPairingCode("");
      setNowPlayingType("playlist");
      setNowPlayingId("");
    }
  }, [existingScreen]);

  useEffect(() => {
    const fetchAvailableContent = async () => {
      try {
        const collectionName =
          nowPlayingType === "media"
            ? "media"
            : nowPlayingType === "schedule"
            ? "schedules"
            : "playlists";
        const contentRef = collection(db, collectionName);
        const querySnapshot = await getDocs(contentRef);
        const content = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setAvailableContent(content);
      } catch (err) {
        console.error("Error fetching content:", err.message);
      }
    };

    fetchAvailableContent();
  }, [nowPlayingType]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const nowPlaying = {
        id: nowPlayingId,
        type: nowPlayingType,
        name:
          availableContent.find((item) => item.id === nowPlayingId)?.name ||
          "Unknown",
      };

      let playerId = null;
      if (pairingCode) {
        const playersRef = collection(db, "players");
        const playerQuery = query(playersRef, where("pairingCode", "==", pairingCode));
        const querySnapshot = await getDocs(playerQuery);

        if (querySnapshot.empty) {
          setError("Invalid pairing code. Please try again.");
          return;
        }

        const playerDoc = querySnapshot.docs[0];
        playerId = playerDoc.id;

        // ✅ First, reset the player's `nowPlaying` field to avoid content merging
        await updateDoc(doc(db, "players", playerId), {
          status: "Paired",
          screenId: existingScreen?.id || null,
          nowPlaying: null, // 🔥 Clear any previous content to avoid content merging
        });

        // ✅ Then, assign the new content
        await updateDoc(doc(db, "players", playerId), {
          nowPlaying,
        });

        console.log("✅ Player updated with nowPlaying content");
      }

      if (existingScreen) {
        const screenRef = doc(db, "screens", existingScreen.id);
        await updateDoc(screenRef, {
          name,
          pairingCode,
          playerId,
          nowPlaying,
        });

        console.log("✅ Screen updated:", existingScreen.id);
      } else {
        const docRef = await addDoc(collection(db, "screens"), {
          name,
          pairingCode,
          playerId,
          nowPlaying,
          createdAt: new Date(),
        });

        if (playerId) {
          await updateDoc(doc(db, "players", playerId), {
            screenId: docRef.id,
          });
        }

        console.log("✅ New screen added with ID:", docRef.id);
      }

      onClose(); // Close the modal
    } catch (err) {
      console.error("❌ Error adding/updating screen:", err.message);
      setError("Failed to save the screen. Please try again.");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-container">
        <h2>{existingScreen ? "Edit Screen" : "Add New Screen"}</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Screen Name:</label>
            <input
              type="text"
              value={name}
              placeholder="Enter screen name"
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>
          <div className="form-group">
            <label>Pairing Code:</label>
            <input
              type="text"
              value={pairingCode}
              placeholder="Enter pairing code from player"
              onChange={(e) => setPairingCode(e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>Now Playing Type:</label>
            <select
              value={nowPlayingType}
              onChange={(e) => setNowPlayingType(e.target.value)}
            >
              <option value="playlist">Playlist</option>
              <option value="media">Media</option>
              <option value="schedule">Schedule</option>
            </select>
          </div>
          <div className="form-group">
            <label>Select Now Playing:</label>
            <select
              value={nowPlayingId}
              onChange={(e) => setNowPlayingId(e.target.value)}
            >
              <option value="">Select a {nowPlayingType}</option>
              {availableContent.map((content) => (
                <option key={content.id} value={content.id}>
                  {content.name}
                </option>
              ))}
            </select>
          </div>
          <div className="modal-actions">
            <button type="button" onClick={onClose} className="btn cancel">
              Cancel
            </button>
            <button type="submit" className="btn save">
              Save
            </button>
          </div>
        </form>
        {error && <p className="error-message">{error}</p>}
      </div>
    </div>
  );
}

export default AddEditScreenModal;
